import { addDays, format, isSameDay } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { useToday } from "../../../hooks/track/useToday";
import { useClientTranslations } from "../../../lib/i18n/client";
import { getEventTypeColor } from "../../../lib/track/eventTypeColor";
import type { Event } from "../../../types/event";
import styles from "./TwoWeekCalendar.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

interface TwoWeekCalendarProps {
	windowStart: Date;
	events: Event[];
	onDayTap: (dayIso: string, eventsForDay: Event[]) => void;
	/**
	 * Phase 38 D-12 — ISO yyyy-MM-dd. The day cell whose date matches this
	 * value pulses for ~1.5s. Triggered whenever `focusDayIso` is set to a
	 * matching value — including post-mount updates (e.g., when a deep-link
	 * notification is tapped while TrackPage is already mounted, the cell
	 * re-pulses on each consumption).
	 */
	focusDayIso?: string | null;
}

export function TwoWeekCalendar({
	windowStart,
	events,
	onDayTap,
	focusDayIso,
}: TwoWeekCalendarProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const { today } = useToday();

	const days = Array.from({ length: 14 }, (_, i) => addDays(windowStart, i));

	// Pitfall 2: day-grouping via slice (NOT parseISO) so wall-clock TZ semantic preserved.
	const byDay = new Map<string, Event[]>();
	for (const e of events) {
		const key = e.dateTime.slice(0, 10);
		const arr = byDay.get(key) ?? [];
		arr.push(e);
		byDay.set(key, arr);
	}

	const dayHeaders = Array.from({ length: 7 }, (_, i) =>
		format(addDays(windowStart, i), "EEEEEE", {
			locale: dateFnsLocale,
		}).toUpperCase(),
	);

	return (
		<div className={styles.calendar}>
			<div className={styles.dayHeaderRow}>
				{dayHeaders.map((header, idx) => (
					<div key={`${header}-${idx}`} className={styles.dayHeader}>
						{header}
					</div>
				))}
			</div>
			<div className={styles.cells}>
				{days.map((d) => {
					const iso = format(d, "yyyy-MM-dd");
					const dayEvents = byDay.get(iso) ?? [];
					const hasEvents = dayEvents.length > 0;
					const isToday = isSameDay(d, today);
					const isFocusDay = focusDayIso === iso;
					const colors = Array.from(
						new Set(dayEvents.map((e) => getEventTypeColor(e.type))),
					).slice(0, 4);
					const cellClass = [
						styles.cell,
						isToday ? styles.cellToday : "",
						!hasEvents ? styles.cellEmpty : "",
						isFocusDay ? styles.cellPulse : "",
					]
						.filter(Boolean)
						.join(" ");
					return (
						<button
							key={iso}
							type="button"
							className={cellClass}
							disabled={!hasEvents}
							aria-current={isToday ? "date" : undefined}
							aria-label={t("calendar.dayCellLabel", {
								date: format(d, "PPPP", { locale: dateFnsLocale }),
								count: dayEvents.length,
							})}
							onClick={() => hasEvents && onDayTap(iso, dayEvents)}
						>
							<span className={styles.dayNumber}>{format(d, "d")}</span>
							{colors.length > 0 && (
								<div className={styles.dotsRow}>
									{colors.map((c) => (
										<span
											key={c}
											className={`${styles.dot} ${styles[`dot--${c}`]}`}
											aria-hidden="true"
										/>
									))}
								</div>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}
